
import React, { useState, useContext, useEffect } from 'react'
import { Button, Form, Alert } from 'react-bootstrap'
import { useHistory } from 'react-router-dom'
import { AuthContext } from '../contexts/AuthContext'
import PasswordInput from '../components/common/PasswordInput'

const MFA_RESEND_COOLDOWN_SECONDS = 60

function sanitizeMfaCode(value) {
    return value.replace(/\D/g, '').slice(0, 6)
}

export default function Login() {
    const history = useHistory()
    const [email, setEmail] = useState("")
    const [password, setPassword] = useState("")
    const [challengeId, setChallengeId] = useState("")
    const [code, setCode] = useState("")
    const [cooldown, setCooldown] = useState(0)
    const [error, setError] = useState("")
    const [locked, setLocked] = useState(false)
    const [loading, setLoading] = useState(false)
    const { login, verifyLoginMfa, resendLoginMfa, user } = useContext(AuthContext)

    useEffect(() => {
        if (user) {
            history.push("/admin");
        }
    }, [user, history]);

    useEffect(() => {
        if (!challengeId || cooldown <= 0) return undefined
        const timer = window.setInterval(() => {
            setCooldown(current => Math.max(0, current - 1))
        }, 1000)
        return () => window.clearInterval(timer)
    }, [challengeId, cooldown])

    async function handleLogin(event) {
        event.preventDefault()
        setLoading(true)
        setError("")
        setLocked(false)
        try {
            const result = await login(email, password)
            if (result.mfaRequired) {
                setChallengeId(result.challengeId)
                setCode("")
                setCooldown(result.retryAfterSeconds || MFA_RESEND_COOLDOWN_SECONDS)
            } else {
                history.push("/admin")
            }
        } catch (err) {
            if (err.response?.status === 423) {
                setLocked(true)
                setError(err.response.data?.msg || "Account locked. Please reset your password to regain access.")
            } else {
                setError("Login failed. Please check your credentials.")
            }
        }
        setLoading(false)
    }

    async function handleVerify(event) {
        event.preventDefault()
        if (code.length !== 6) {
            setError("Enter the six-digit code from your email.")
            return
        }
        setLoading(true)
        setError("")
        try {
            await verifyLoginMfa(challengeId, code)
            history.push("/admin")
        } catch (err) {
            setError(err.response?.data?.msg || "Unable to verify the code. Please try again.")
        }
        setLoading(false)
    }

    async function handleResend() {
        setLoading(true)
        setError("")
        try {
            const result = await resendLoginMfa(challengeId)
            setChallengeId(result.challengeId)
            setCode("")
            setCooldown(result.retryAfterSeconds || MFA_RESEND_COOLDOWN_SECONDS)
        } catch (err) {
            const retryAfter = Number(err.response?.data?.retry_after_seconds)
            if (err.response?.status === 429 && retryAfter > 0) {
                setCooldown(retryAfter)
            }
            setError(err.response?.data?.msg || "Unable to send a new code. Please try again.")
        }
        setLoading(false)
    }

    function handleBackToLogin() {
        setChallengeId("")
        setCode("")
        setPassword("")
        setCooldown(0)
        setError("")
    }

    return (
        <div className="auth-container">
            <div className="auth-card">
                <h2 className="auth-title">{challengeId ? "Verify Your Login" : "Log In"}</h2>
                {error && (
                    <Alert id="login-error" variant="danger" role="alert">
                        {error}
                        {locked && <> <Alert.Link href="/forgot-password">Reset your password</Alert.Link></>}
                    </Alert>
                )}
                {challengeId ? (
                    <>
                        <p id="mfa-help" style={{ marginBottom: '1rem' }}>
                            Enter the six-digit code emailed to you. Requesting a new code invalidates the previous one.
                        </p>
                        <Form onSubmit={handleVerify}>
                            <Form.Group controlId="mfa-code" style={{ display: 'flex', flexDirection: 'column' }}>
                                <Form.Label>Verification code</Form.Label>
                                <Form.Control
                                    className="auth-input"
                                    type="text"
                                    inputMode="numeric"
                                    autoComplete="one-time-code"
                                    pattern="[0-9]{6}"
                                    maxLength={6}
                                    aria-invalid={Boolean(error)}
                                    aria-describedby={error ? "mfa-help login-error" : "mfa-help"}
                                    value={code}
                                    onChange={event => {
                                        setCode(sanitizeMfaCode(event.target.value))
                                        setError("")
                                    }}
                                    required
                                    autoFocus
                                />
                            </Form.Group>
                            <Button className="auth-btn auth-btn-primary" type="submit" disabled={loading || code.length !== 6}>
                                {loading ? "Verifying..." : "Verify code"}
                            </Button>
                        </Form>
                        <Button
                            className="auth-btn"
                            variant="outline-primary"
                            type="button"
                            disabled={loading || cooldown > 0}
                            onClick={handleResend}
                        >
                            {cooldown > 0 ? `Resend available in ${cooldown}s` : "Send a new code"}
                        </Button>
                        <Button className="auth-btn" variant="link" type="button" disabled={loading} onClick={handleBackToLogin}>
                            Back to login
                        </Button>
                    </>
                ) : (
                    <>
                        <Form onSubmit={handleLogin}>
                            <Form.Group id="email" style={{ display: 'flex', flexDirection: 'column' }}>
                                <Form.Label>Email</Form.Label>
                                <Form.Control className="auth-input" type="email" value={email} onChange={e => setEmail(e.target.value)} required />
                            </Form.Group>
                            <Form.Group id="password" style={{ display: 'flex', flexDirection: 'column' }}>
                                <Form.Label>Password</Form.Label>
                                <PasswordInput className="auth-input" value={password} onChange={e => setPassword(e.target.value)} required />
                            </Form.Group>
                            <Button className="auth-btn auth-btn-primary" type="submit" disabled={loading}>
                                {loading ? "Logging in..." : "Log In"}
                            </Button>
                        </Form>
                        <a className="auth-link" href="/forgot-password">Forgot password?</a>
                    </>
                )}
            </div>
        </div>
    )
}